// src/components/Recruiter/CVPDFTemplate.jsx
import React from 'react';

const CVPDFTemplate = ({ candidate, contactUnlocked }) => {
    // Experience level labels
    const experienceLabels = {
        'NO_EXPERIENCE': 'No Experience',
        'ZERO_TO_ONE_YEAR': '0 - 1 Year',
        'ONE_TO_TWO_YEARS': '1 - 2 Years'
    };

    const getExperienceLabel = (level) => {
        return experienceLabels[level] || level || 'Not specified';
    };

    // Render bullet list
    const renderBulletList = (items) => {
        if (!items || items.length === 0) return null;
        return (
            <ul className="list-disc list-inside text-sm text-black/80 space-y-0.5">
                {items.map((item, idx) => (
                    <li key={idx}>{item}</li>
                ))}
            </ul>
        );
    };

    return (
        <div id="cv-pdf-content" className="bg-white p-8 max-w-4xl mx-auto" style={{ fontFamily: 'Times New Roman, serif' }}>
            {/* Header - Clean and classic */}
            <div className="border-b-2 border-black pb-4 mb-6">
                <h1 className="text-3xl font-bold text-black uppercase tracking-wide">
                    {candidate.firstName} {candidate.lastName}
                </h1>
                <p className="text-lg text-black/70 mt-1">
                    {candidate.currentJobTitle || 'Candidate'}
                </p>
            </div>

            <div className="mt-6">
                {/* Contact Info - Only show if unlocked */}
                {contactUnlocked && (
                    <div className="mb-6">
                        <h2 className="text-base font-bold text-black uppercase tracking-wider border-b border-black pb-1 mb-3">
                            Contact Details
                        </h2>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                            {candidate.email && (
                                <div>
                                    <span className="font-semibold">Email:</span>
                                    <span className="ml-2 text-black/80 break-all">{candidate.email}</span>
                                </div>
                            )}
                            {candidate.phoneNumber && (
                                <div>
                                    <span className="font-semibold">Phone:</span>
                                    <span className="ml-2 text-black/80">{candidate.phoneNumber}</span>
                                </div>
                            )}
                            {candidate.city && (
                                <div>
                                    <span className="font-semibold">Location:</span>
                                    <span className="ml-2 text-black/80">{candidate.city}, {candidate.province}</span>
                                </div>
                            )}
                            {candidate.linkedin && (
                                <div className="col-span-2">
                                    <span className="font-semibold">LinkedIn:</span>
                                    <span className="ml-2 text-black/80 break-all">{candidate.linkedin}</span>
                                </div>
                            )}
                            {candidate.portfolio && (
                                <div className="col-span-2">
                                    <span className="font-semibold">Portfolio:</span>
                                    <span className="ml-2 text-black/80 break-all">{candidate.portfolio}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Professional Summary */}
                {candidate.professionalSummary && (
                    <div className="mb-6">
                        <h2 className="text-base font-bold text-black uppercase tracking-wider border-b border-black pb-1 mb-3">
                            Professional Summary
                        </h2>
                        <p className="text-sm text-black/80 leading-relaxed">{candidate.professionalSummary}</p>
                    </div>
                )}

                {/* Skills - Bullet Points */}
                {candidate.skills?.length > 0 && (
                    <div className="mb-6">
                        <h2 className="text-base font-bold text-black uppercase tracking-wider border-b border-black pb-1 mb-3">
                            Skills
                        </h2>
                        {renderBulletList(candidate.skills)}
                    </div>
                )}

                {/* Languages - Bullet Points */}
                {candidate.languages?.length > 0 && (
                    <div className="mb-6">
                        <h2 className="text-base font-bold text-black uppercase tracking-wider border-b border-black pb-1 mb-3">
                            Languages
                        </h2>
                        {renderBulletList(candidate.languages)}
                    </div>
                )}

                {/* Experience Level */}
                {candidate.experienceLevel && (
                    <div className="mb-6">
                        <h2 className="text-base font-bold text-black uppercase tracking-wider border-b border-black pb-1 mb-3">
                            Experience Level
                        </h2>
                        <p className="text-sm text-black/80">{getExperienceLabel(candidate.experienceLevel)}</p>
                    </div>
                )}

                {/* Work Experience */}
                {candidate.workExperience?.length > 0 && (
                    <div className="mb-6">
                        <h2 className="text-base font-bold text-black uppercase tracking-wider border-b border-black pb-1 mb-3">
                            Work Experience
                        </h2>
                        {candidate.workExperience.map((exp, index) => (
                            <div key={index} className="mb-3">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="font-bold text-sm text-black">{exp.title}</p>
                                        <p className="text-sm text-black/70">{exp.company}</p>
                                    </div>
                                    <p className="text-sm text-black/60 whitespace-nowrap ml-4">
                                        {exp.startDate || 'Start date'} — {exp.isCurrent ? 'Present' : exp.endDate || 'End date'}
                                    </p>
                                </div>
                                {exp.description && (
                                    <p className="text-sm text-black/70 mt-1 leading-relaxed">{exp.description}</p>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Education */}
                {candidate.education?.length > 0 && (
                    <div className="mb-6">
                        <h2 className="text-base font-bold text-black uppercase tracking-wider border-b border-black pb-1 mb-3">
                            Education
                        </h2>
                        {candidate.education.map((edu, index) => (
                            <div key={index} className="mb-3">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="font-bold text-sm text-black">{edu.qualification}</p>
                                        <p className="text-sm text-black/70">{edu.institution}</p>
                                    </div>
                                    <p className="text-sm text-black/60 whitespace-nowrap ml-4">
                                        {edu.startDate || ''} {edu.isCurrent ? '— Present' : edu.endDate ? `— ${edu.endDate}` : ''}
                                    </p>
                                </div>
                                {edu.keyHighlights && (
                                    <p className="text-sm text-black/70 mt-1 leading-relaxed">
                                        {edu.keyHighlights}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Certifications */}
                {candidate.certifications?.length > 0 && (
                    <div className="mb-6">
                        <h2 className="text-base font-bold text-black uppercase tracking-wider border-b border-black pb-1 mb-3">
                            Certifications & Awards
                        </h2>
                        {candidate.certifications.map((cert, index) => (
                            <div key={index} className="mb-2">
                                <p className="font-bold text-sm text-black">{cert.name}</p>
                                {cert.issuingOrganization && (
                                    <p className="text-sm text-black/70">{cert.issuingOrganization}</p>
                                )}
                                {cert.year && (
                                    <p className="text-sm text-black/60">{cert.year}</p>
                                )}
                                {cert.description && (
                                    <p className="text-sm text-black/70 mt-1">{cert.description}</p>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* References - Only show if unlocked */}
                {contactUnlocked && candidate.references?.length > 0 && (
                    <div>
                        <h2 className="text-base font-bold text-black uppercase tracking-wider border-b border-black pb-1 mb-3">
                            References
                        </h2>
                        {candidate.references.map((ref, index) => (
                            <div key={index} className="mb-2">
                                <p className="font-bold text-sm text-black">{ref.name}</p>
                                {ref.title && <p className="text-sm text-black/70">{ref.title}</p>}
                                {ref.company && <p className="text-sm text-black/70">{ref.company}</p>}
                                {(ref.email || ref.phone) && (
                                    <div className="text-sm text-black/60">
                                        {ref.email && <span>{ref.email}</span>}
                                        {ref.email && ref.phone && <span className="mx-2">•</span>}
                                        {ref.phone && <span>{ref.phone}</span>}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Footer */}
                <div className="mt-8 pt-4 border-t border-black/20 text-center text-xs text-black/40">
                    Generated from GradPath — {new Date().toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
            </div>
        </div>
    );
};

export default CVPDFTemplate;